import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarAlt,
  faCheckCircle,
  faCheck,
  faSpinner,
  faTimes,
  faExclamationTriangle,
  faChevronDown
} from '@fortawesome/free-solid-svg-icons';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import googleCalendarService from '../../api/googleCalendarService';
import '../../styles/group/GoogleCalendarImport.css';

const GoogleCalendarImport = ({ isOpen, onClose, groupId, onEventsImported, calendars = [], activeCalendar = null }) => {
  const [step, setStep] = useState('connect'); // connect, select, confirm, importing, complete
  const [isConnected, setIsConnected] = useState(false);
  const [connectedAccount, setConnectedAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedEvents, setSelectedEvents] = useState(new Set());
  const [eventCategories, setEventCategories] = useState({}); // Store category for each event index
  const [selectedCalendar, setSelectedCalendar] = useState(activeCalendar || null); // Single calendar selection
  const [error, setError] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [hasCheckedConnection, setHasCheckedConnection] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isCalendarDropdownOpen, setIsCalendarDropdownOpen] = useState(false);
  const calendarDropdownRef = useRef(null);

  // Reset everything when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Modal is closed - reset everything
      console.log('Modal closed, resetting state');
      setStep('connect');
      setIsConnected(false);
      setEvents([]);
      setSelectedEvents(new Set());
      setEventCategories({});
      setSelectedCalendar(activeCalendar || null); // Reset to active calendar
      setError(null);
      setLoading(false);
      setImportResults(null);
      setHasCheckedConnection(false);
      setIsClosing(false);
      setIsCalendarDropdownOpen(false);
    }
  }, [isOpen, activeCalendar]);

  // Click outside dropdown to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarDropdownRef.current && !calendarDropdownRef.current.contains(event.target)) {
        setIsCalendarDropdownOpen(false);
      }
    };

    if (isCalendarDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isCalendarDropdownOpen]);

  // Check connection only once when modal first opens (and not if we're closing)
  useEffect(() => {
    if (isOpen && groupId && !hasCheckedConnection && !isClosing) {
      console.log('Modal opened, checking connection for group:', groupId);
      setHasCheckedConnection(true);
      checkConnection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, groupId, hasCheckedConnection, isClosing]);

  // Listen for OAuth callback results
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const gcalStatus = urlParams.get('gcal');
    const reason = urlParams.get('reason');

    if (gcalStatus === 'connected') {
      // Connection successful
      if (window.showToast) {
        window.showToast('Google Calendar connected successfully!', 'success');
      }
      // Clear the URL parameters
      window.history.replaceState({}, '', window.location.pathname);
    } else if (gcalStatus === 'error') {
      // Connection failed
      let errorMessage = 'Failed to connect Google Calendar.';
      if (reason === 'oauth_denied') {
        errorMessage = 'You denied access to Google Calendar.';
      } else if (reason === 'no_code') {
        errorMessage = 'No authorization code received from Google.';
      } else if (reason === 'invalid_state') {
        errorMessage = 'Invalid authentication state. Please try again.';
      } else if (reason === 'missing_params') {
        errorMessage = 'Missing required parameters. Please try again.';
      } else if (reason === 'server_error') {
        errorMessage = 'Server error occurred. Please check the logs.';
      }
      
      setError(errorMessage);
      
      if (window.showToast) {
        window.showToast(errorMessage, 'error');
      }
      // Clear the URL parameters
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [isOpen]);

  // Check if Google Calendar is connected
  const checkConnection = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      
      console.log('Checking Google Calendar connection...');
      const response = await googleCalendarService.checkConnectionStatus(groupId);
      const connected = response.connected || false;
      const accountEmail = response.accountEmail || null;
      
      console.log('Connection status:', connected, 'Account:', accountEmail);
      
      setIsConnected(connected);
      setConnectedAccount(accountEmail);
      
      if (connected) {
        // If already connected, skip to event selection
        console.log('Already connected, fetching events...');
        setStep('select');
        await fetchGoogleEvents();
      } else {
        console.log('Not connected, showing connect screen');
        setStep('connect');
      }
    } catch (err) {
      console.error('Error checking connection:', err);
      setError('Failed to check Google Calendar connection status');
      setIsConnected(false);
      setConnectedAccount(null);
      setStep('connect'); // Reset to connect screen on error
    } finally {
      setLoading(false);
    }
  };

  // Connect to Google Calendar
  const handleConnect = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get authorization URL
      const authUrl = await googleCalendarService.getAuthUrl(groupId);
      
      // Open Google OAuth in a popup window
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      const popup = window.open(
        authUrl,
        'Google Calendar Authorization',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Track whether we've handled the auth response
      let authHandled = false;

      // Listen for messages from the popup
      const handleMessage = (event) => {
        // Verify message is from our popup
        if (event.data.type === 'GOOGLE_CALENDAR_AUTH' && !authHandled) {
          authHandled = true;
          window.removeEventListener('message', handleMessage);
          clearInterval(checkPopup);
          
          if (event.data.success) {
            // Success! Connection established
            setIsConnected(true);
            setStep('select');
            fetchGoogleEvents();
          } else {
            // Error occurred
            setError(event.data.error || 'Failed to connect to Google Calendar');
            setLoading(false);
          }
        }
      };

      window.addEventListener('message', handleMessage);

      // Fallback: Poll for popup closure (in case postMessage doesn't work)
      const checkPopup = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkPopup);
          window.removeEventListener('message', handleMessage);
          
          // Only check connection if we haven't already handled the auth
          if (!authHandled) {
            authHandled = true;
            setTimeout(() => {
              checkConnection();
            }, 1000);
          }
        }
      }, 500);
      
    } catch (err) {
      console.error('Error connecting to Google Calendar:', err);
      setError('Failed to connect to Google Calendar. Please try again.');
      setLoading(false);
    }
  };

  // Disconnect Google Calendar
  const handleDisconnect = async () => {
    try {
      setLoading(true);
      setError(null);
      
      await googleCalendarService.disconnect(groupId);
      
      setIsConnected(false);
      setConnectedAccount(null);
      setEvents([]);
      setSelectedEvents(new Set());
      setEventCategories({});
      setStep('connect');
      
      if (window.showToast) {
        window.showToast('Google Calendar disconnected successfully', 'success');
      }
    } catch (err) {
      console.error('Error disconnecting Google Calendar:', err);
      setError('Failed to disconnect Google Calendar. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch events from Google Calendar
  const fetchGoogleEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching Google Calendar events...');
      const fetchedEvents = await googleCalendarService.fetchEvents(groupId, {
        maxResults: 100
      });
      
      console.log('Fetched events:', fetchedEvents.length);
      setEvents(fetchedEvents);
      
      if (fetchedEvents.length === 0) {
        setError('No upcoming events found in your Google Calendar');
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      
      // Check if it's an auth error
      if (err.message?.includes('not authenticated') || err.message?.includes('expired')) {
        setError('Your Google Calendar connection has expired. Please reconnect.');
        setIsConnected(false);
        setConnectedAccount(null);
        setStep('connect');
      } else {
        setError('Failed to fetch events from Google Calendar. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Toggle event selection
  const toggleEventSelection = (eventId) => {
    const newSelected = new Set(selectedEvents);
    if (newSelected.has(eventId)) {
      newSelected.delete(eventId);
      // Remove category when deselecting
      const newCategories = { ...eventCategories };
      delete newCategories[eventId];
      setEventCategories(newCategories);
    } else {
      newSelected.add(eventId);
      // Set default category to 'event' when selecting
      setEventCategories({ ...eventCategories, [eventId]: 'event' });
    }
    setSelectedEvents(newSelected);
  };

  // Set category for a specific event
  const setEventCategory = (index, category) => {
    setEventCategories(prev => ({
      ...prev,
      [index]: category
    }));
  };

  // Bulk set category for all selected events
  const setBulkCategory = (category) => {
    const newCategories = { ...eventCategories };
    selectedEvents.forEach(index => {
      newCategories[index] = category;
    });
    setEventCategories(newCategories);
  };

  // Select all events
  const selectAll = () => {
    const allIds = new Set(events.map((_, index) => index));
    setSelectedEvents(allIds);
    // Set default category for all newly selected events
    const newCategories = { ...eventCategories };
    events.forEach((_, index) => {
      if (!newCategories[index]) {
        newCategories[index] = 'event';
      }
    });
    setEventCategories(newCategories);
  };

  // Deselect all events
  const deselectAll = () => {
    setSelectedEvents(new Set());
    setEventCategories({});
  };

  // Proceed to confirmation step
  const handleProceedToConfirm = () => {
    if (selectedEvents.size === 0) {
      setError('Please select at least one event to import');
      return;
    }
    
    // Check if all selected events have categories
    const missingCategories = Array.from(selectedEvents).filter(
      index => !eventCategories[index]
    );
    
    if (missingCategories.length > 0) {
      setError('Please assign a category (Event/Class/Other) to all selected items');
      return;
    }
    
    setStep('confirm');
  };

  // Import selected events
  const handleImport = async () => {
    // Validate calendar selection
    if (!selectedCalendar) {
      setError('Please select a calendar to import to');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setStep('importing');
      
      // Get selected event data with their categories, Google Calendar IDs, and target calendar
      const eventsToImport = Array.from(selectedEvents).map(index => ({
        ...events[index],
        category: eventCategories[index] || 'event',
        googleCalendarId: events[index].id || events[index].googleCalendarId, // Use Google's event ID
        calendarIds: [selectedCalendar] // Single calendar ID in array format for backend compatibility
      }));
      
      // Import events with categories and calendar associations
      const results = await googleCalendarService.importEvents(groupId, eventsToImport);
      
      setImportResults(results);
      setStep('complete');
      
      // DON'T notify parent yet - wait until user closes the modal
      // This prevents the parent from re-rendering and reopening the modal
    } catch (err) {
      console.error('Error importing events:', err);
      setError('Failed to import events. Please try again.');
      setStep('confirm');
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Close modal
  const handleClose = () => {
    // Set closing flag to prevent re-opening
    setIsClosing(true);
    
    // Notify parent component if we have import results (successful import)
    if (importResults && onEventsImported) {
      onEventsImported(importResults);
    }
    
    // Close the modal
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          {connectedAccount && step === 'select' && (
            <div className="header-account-info">
              <span className="header-account-email">
                <FontAwesomeIcon icon={faGoogle} className="google-icon-small" />
                {connectedAccount}
              </span>
              <button 
                className="btn-disconnect-header" 
                onClick={handleDisconnect}
                disabled={loading}
                title="Disconnect and use a different account"
              >
                Disconnect
              </button>
            </div>
          )}
          <h2>Import from Google Calendar</h2>
          <button className="modal-close-btn" onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          {/* Error Alert */}
          {error && (
            <div className="error-alert">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>{error}</span>
            </div>
          )}

          {/* Content based on step */}
          
          {/* Step 1: Connect */}
          {step === 'connect' && (
            <div className="connect-step">
              <div className="connect-illustration">
                <FontAwesomeIcon icon={faGoogle} size="4x" className="google-icon-large" />
                <FontAwesomeIcon icon={faCalendarAlt} size="4x" className="calendar-icon-large" />
              </div>
              <h3>Connect your Google Calendar</h3>
              <p>Import events from your Google Calendar to quickly populate your community calendar.</p>
              <p className="privacy-note">We only request read-only access to your calendar.</p>
              <button 
                className="btn-connect-google" 
                onClick={handleConnect}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Connecting...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faGoogle} />
                    <span>Connect Google Calendar</span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* Step 2: Select Events */}
          {step === 'select' && (
            <div className="select-step">
              <div className="select-events-header">
                <h3>Select Events to Import</h3>
              </div>

              {/* Calendar Selection - Compact Inline */}
              {calendars && calendars.length > 0 && (
                <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-200">
                  <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                    Import to:
                  </label>
                  
                  <div className="relative flex-1" ref={calendarDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setIsCalendarDropdownOpen(!isCalendarDropdownOpen)}
                      className="w-full max-w-md flex items-center justify-between px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <span className="flex-1 text-left truncate">
                        {!selectedCalendar && (
                          <span className="text-gray-400">Select calendar</span>
                        )}
                        {selectedCalendar && calendars.find(c => c.id === selectedCalendar) && (
                          <span className="flex items-center gap-1.5">
                            <span
                              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: calendars.find(c => c.id === selectedCalendar)?.color || '#6b7280' }}
                            ></span>
                            <span className="text-gray-900 truncate">
                              {calendars.find(c => c.id === selectedCalendar)?.name}
                            </span>
                          </span>
                        )}
                      </span>
                      <FontAwesomeIcon 
                        icon={faChevronDown} 
                        className={`ml-2 text-gray-400 transition-transform text-xs ${isCalendarDropdownOpen ? 'rotate-180' : ''}`}
                      />
                    </button>

                    {isCalendarDropdownOpen && (
                      <div className="absolute z-50 mt-1 w-full max-w-md bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-auto">
                        {calendars.map((calendar) => (
                          <div
                            key={calendar.id}
                            onClick={() => {
                              setSelectedCalendar(calendar.id);
                              setIsCalendarDropdownOpen(false);
                            }}
                            className={`flex items-center px-3 py-2 cursor-pointer transition-colors ${
                              selectedCalendar === calendar.id ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'
                            }`}
                          >
                            <span
                              className="w-2.5 h-2.5 rounded-full mr-2 flex-shrink-0"
                              style={{ backgroundColor: calendar.color || '#6b7280' }}
                            ></span>
                            <span className="text-sm text-gray-900 truncate flex-1">
                              {calendar.name}
                            </span>
                            {calendar.is_default && (
                              <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                                Default
                              </span>
                            )}
                            {selectedCalendar === calendar.id && (
                              <FontAwesomeIcon icon={faCheck} className="ml-2 text-blue-600 text-xs" />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {!selectedCalendar && (
                    <span className="text-xs text-red-500 whitespace-nowrap">
                      Required
                    </span>
                  )}
                </div>
              )}

              {loading ? (
                <div className="loading-container">
                  <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                  <p>Loading events from Google Calendar...</p>
                </div>
              ) : events.length === 0 ? (
                <div className="empty-state">
                  <FontAwesomeIcon icon={faCalendarAlt} size="3x" className="empty-icon" />
                  <p>No upcoming events found in your Google Calendar</p>
                  <button className="btn-secondary" onClick={fetchGoogleEvents}>
                    Refresh
                  </button>
                </div>
              ) : (
                <div className="events-table-container">
                  <div className="events-count">
                    {selectedEvents.size} of {events.length} events selected
                    {events.filter(e => e.isAlreadyImported).length > 0 && (
                      <span className="imported-count">
                        {' '}• {events.filter(e => e.isAlreadyImported).length} already imported
                      </span>
                    )}
                  </div>
                  
                  <div className="events-table-wrapper">
                    <table className="events-table">
                      <thead>
                        <tr>
                          <th className="col-checkbox">
                            <input
                              type="checkbox"
                              checked={selectedEvents.size === events.length}
                              onChange={() => {
                                if (selectedEvents.size === events.length) {
                                  deselectAll();
                                } else {
                                  selectAll();
                                }
                              }}
                            />
                          </th>
                          <th className="col-title">Title</th>
                          <th className="col-description">Description</th>
                          <th className="col-date">Date & Time</th>
                          <th className="col-location">Location</th>
                          <th className="col-category">
                            {selectedEvents.size > 1 ? (
                              <div className="bulk-category-header">
                                <span>Import As</span>
                                <select
                                  className="bulk-category-select"
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      setBulkCategory(e.target.value);
                                      e.target.value = ''; // Reset dropdown
                                    }
                                  }}
                                  defaultValue=""
                                >
                                  <option value="">Bulk Apply...</option>
                                  <option value="event">Event to All</option>
                                  <option value="class">Class to All</option>
                                  <option value="other">Other to All</option>
                                </select>
                              </div>
                            ) : (
                              'Import As'
                            )}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {events.map((event, index) => (
                          <tr 
                            key={index}
                            className={`
                              ${selectedEvents.has(index) ? 'selected' : ''} 
                              ${event.isAlreadyImported ? 'already-imported' : ''}
                            `.trim()}
                          >
                            <td className="col-checkbox">
                              <input
                                type="checkbox"
                                checked={selectedEvents.has(index)}
                                onChange={() => toggleEventSelection(index)}
                                disabled={event.isAlreadyImported}
                              />
                            </td>
                            <td className="col-title">
                              <div className="title-with-badge">
                                <strong>{event.title}</strong>
                                {event.isAlreadyImported && (
                                  <span className="imported-badge" title="Already imported">
                                    <FontAwesomeIcon icon={faCheckCircle} /> Imported
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="col-description">
                              {event.description ? (
                                <span className="description-text">
                                  {event.description.substring(0, 80)}
                                  {event.description.length > 80 ? '...' : ''}
                                </span>
                              ) : (
                                <span className="no-description">—</span>
                              )}
                            </td>
                            <td className="col-date">
                              {formatDate(event.date)}
                            </td>
                            <td className="col-location">
                              {event.location ? (
                                <span className="location-text">📍 {event.location}</span>
                              ) : (
                                <span className="no-location">—</span>
                              )}
                            </td>
                            <td className="col-category">
                              <select
                                className="category-select"
                                value={eventCategories[index] || 'event'}
                                onChange={(e) => setEventCategory(index, e.target.value)}
                                disabled={!selectedEvents.has(index) || event.isAlreadyImported}
                              >
                                <option value="event">Event</option>
                                <option value="class">Class</option>
                                <option value="other">Other</option>
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Confirm */}
          {step === 'confirm' && (
            <div className="text-center py-16 px-8">
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-0">Confirm Import</h3>
              </div>

              <div className="max-w-lg mx-auto">
                <p className="text-base text-gray-600 leading-relaxed mb-4">
                  You are about to import <strong className="text-gray-900 font-bold">{selectedEvents.size}</strong> item{selectedEvents.size !== 1 ? 's' : ''} to your community calendar, which will be visible to your members on the Yesod app.
                </p>
                <p className="text-lg text-gray-900 font-semibold mt-6">Ready to proceed?</p>
              </div>
            </div>
          )}

          {/* Step 4: Importing (Loading) */}
          {step === 'importing' && (
            <div className="text-center py-16 px-8">
              <FontAwesomeIcon icon={faSpinner} spin className="text-6xl text-blue-600 mb-6" />
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">Importing Events...</h3>
              <p className="text-base text-gray-600">Please wait while we import your events.</p>
            </div>
          )}

          {/* Step 5: Complete */}
          {step === 'complete' && importResults && (
            <div className="text-center py-16 px-8">
              <div className="mb-8">
                <FontAwesomeIcon 
                  icon={faCheckCircle} 
                  className="text-6xl text-green-500 mb-4"
                />
                <h3 className="text-2xl font-semibold text-gray-900 mb-3">Import Complete</h3>
              </div>

              {importResults && (
                <div className="max-w-2xl mx-auto bg-gray-50 rounded-lg p-6 text-left">
                  {importResults.imported > 0 && (
                    <div className="flex items-center gap-3 py-3 text-base text-green-600">
                      <FontAwesomeIcon icon={faCheckCircle} className="flex-shrink-0" />
                      <span><strong>{importResults.imported}</strong> event{importResults.imported !== 1 ? 's' : ''} imported successfully</span>
                    </div>
                  )}
                  
                  {importResults.duplicates > 0 && (
                    <div className="flex items-center gap-3 py-3 text-base text-amber-600">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="flex-shrink-0" />
                      <span><strong>{importResults.duplicates}</strong> duplicate{importResults.duplicates !== 1 ? 's' : ''} skipped</span>
                    </div>
                  )}
                  
                  {importResults.errors > 0 && (
                    <div className="flex items-center gap-3 py-3 text-base text-red-600">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="flex-shrink-0" />
                      <span><strong>{importResults.errors}</strong> error{importResults.errors !== 1 ? 's' : ''} occurred</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer with Actions */}
        <div className="flex justify-end items-center gap-3 px-12 py-4 flex-shrink-0 bg-transparent border-b-0">
          {/* Select step actions */}
          {step === 'select' && !loading && events.length > 0 && (
            <button 
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg text-base font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" 
              onClick={handleProceedToConfirm}
              disabled={selectedEvents.size === 0}
            >
              Continue ({selectedEvents.size} selected)
            </button>
          )}

          {/* Confirm step actions */}
          {step === 'confirm' && (
            <>
              <button 
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gray-700 border border-gray-300 rounded-lg text-base font-semibold hover:bg-gray-50 hover:border-gray-400 transition-colors"
                onClick={() => setStep('select')}
              >
                Back
              </button>
              <button 
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg text-base font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                onClick={handleImport}
                disabled={loading || !selectedCalendar}
                title={!selectedCalendar ? 'Please select a calendar' : ''}
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Importing...</span>
                  </>
                ) : (
                  <>Import Items</>
                )}
              </button>
            </>
          )}

          {/* Complete step actions */}
          {step === 'complete' && (
            <button 
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg text-base font-semibold hover:bg-blue-700 transition-colors"
              onClick={handleClose}
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoogleCalendarImport;
